/**
 * TrainingLayout — standalone full-screen layout for the training viewer.
 *
 * Brand-aware. Reads `?brand=pos` from the URL to render in Peace of Sleep
 * skin (Questrial font, gold accent, linen background, "Delivered by
 * CleanMyBed" footer). Default is CleanMyBed Training (Inter, violet,
 * gray background) for the operator + ambassador audiences.
 *
 * The Charter Suite share panel emits links with `&brand=pos`. Operators
 * navigating from the portal admin pages don't include the flag and stay
 * in CMB skin. Same component, same URLs, two skins.
 */

import { PropsWithChildren } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { POS_GOLD } from '../../config/posRoomStatus';

const VIOLET = 'var(--color-primary)';

interface BrandConfig {
  brandText: string;
  brandColor: string;
  background: string;
  headerBackground: string;
  headerBorder: string;
  fontFamily: string;
  footerText: string;
  loadQuestrial: boolean;
  wrapperClass: string;
}

const BRAND_CONFIG: Record<'cmb' | 'pos', BrandConfig> = {
  cmb: {
    brandText: 'CleanMyBed Training',
    brandColor: VIOLET,
    background: 'var(--color-bg)',
    headerBackground: 'var(--color-white)',
    headerBorder: 'var(--color-border)',
    fontFamily: 'inherit',
    footerText: 'Sentinel Training · Powered by CleanMyBed',
    loadQuestrial: false,
    wrapperClass: 'training-shell training-shell--cmb',
  },
  pos: {
    brandText: 'Peace of Sleep · Training',
    brandColor: POS_GOLD,
    background: '#EAE7E1',
    headerBackground: 'var(--color-sidebar)',
    headerBorder: 'var(--color-sidebar)',
    fontFamily: "'Questrial', -apple-system, sans-serif",
    footerText: 'Peace of Sleep · Delivered by CleanMyBed',
    loadQuestrial: true,
    wrapperClass: 'training-shell training-shell--pos',
  },
};

function TrainingLayout({ children }: PropsWithChildren) {
  const { session } = useAuth();
  const [searchParams] = useSearchParams();
  const isSignedIn = Boolean(session?.user);
  const brand: 'cmb' | 'pos' = searchParams.get('brand') === 'pos' ? 'pos' : 'cmb';
  const cfg = BRAND_CONFIG[brand];

  return (
    <>
      {cfg.loadQuestrial && (
        // Questrial loads inside the layout (not index.html) so it only
        // hits the wire when someone actually visits a POS-skinned page.
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Questrial&display=swap"
        />
      )}
      <div
        className={cfg.wrapperClass}
        style={{
          minHeight: '100vh',
          background: cfg.background,
          fontFamily: cfg.fontFamily,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <header style={{
          background: cfg.headerBackground,
          borderBottom: `1px solid ${cfg.headerBorder}`,
          padding: '0 1.5rem',
          height: '56px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 50,
        }}>
          <div className="flex items-center gap-lg">
            <div style={{
              fontWeight: brand === 'pos' ? 500 : 700,
              fontSize: 'var(--text-xl)',
              letterSpacing: brand === 'pos' ? '1.5px' : '0.5px',
              textTransform: brand === 'pos' ? 'uppercase' : 'none',
              color: cfg.brandColor,
            }}>
              {cfg.brandText}
            </div>
          </div>
          {isSignedIn && (
            <Link
              to="/"
              style={{
                fontSize: 'var(--text-md)',
                color: brand === 'pos' ? 'rgba(255,255,255,0.7)' : 'var(--color-text-light)',
                textDecoration: 'none',
              }}
            >
              Back to Portal &rarr;
            </Link>
          )}
        </header>

        {/* Content */}
        <main style={{
          flex: 1,
          maxWidth: '860px',
          width: '100%',
          margin: '0 auto',
          padding: '2rem 1.5rem',
        }}>
          {children}
        </main>

        {/* Footer */}
        <footer style={{
          textAlign: 'center',
          padding: '1.5rem',
          fontSize: 'var(--text-sm)',
          color: brand === 'pos' ? 'var(--color-text-light)' : 'var(--color-text-light)',
          letterSpacing: brand === 'pos' ? '1px' : 'normal',
        }}>
          {cfg.footerText}
        </footer>
      </div>
    </>
  );
}

export default TrainingLayout;
